import { formatDate } from '@if/core/utils'
import { useConfirmDialog } from '@if/mui'
import { useComputeCalendarAction } from '@src/api/loan/loan-api'
import { ChargeOffOperation } from '@src/types/loan/loan-operation'
import { ECalendarSimulationAction, SignedCalendarSimulation } from '@src/types/loan/loan-simulation'
import { useCallback, useRef } from 'react'
import { useTranslation } from 'react-i18next'

/**
 * Deletes the charge-off of its calendar row, once the agent confirms. Null when the agent backs out. The backend
 * refuses one reported to accounting: its message stays in the open dialog.
 */
export const useDeleteChargeOffOnCalendar = (signedSimulation: SignedCalendarSimulation, isLockedByOther: boolean) => {
  const { t } = useTranslation()
  const { loan } = signedSimulation.simulation.fullLoan
  const { mutateAsync: computeAction } = useComputeCalendarAction(loan.id, loan.financingCompanyId)
  // A confirm dialog resolves with a boolean only: the recomputed calendar comes back through here.
  const resultRef = useRef<SignedCalendarSimulation | null>(null)

  const confirm = useConfirmDialog<ChargeOffOperation>({
    title: t('loan.payment.deleteChargeOffDialog.title'),
    message: ({ props: chargeOff }) =>
      t('loan.payment.deleteChargeOffDialog.message', { date: formatDate(chargeOff.effectiveDate) }),
    confirmLabel: t('loan.payment.deleteChargeOffDialog.confirm'),
    mode: 'destructive',
    confirmDisabled: () => isLockedByOther,
    confirmTooltip: () => (isLockedByOther ? t('common.lockTooltip.lockedByOther') : null),
    onConfirm: async ({ props: chargeOff }) => {
      resultRef.current = await computeAction({
        ...signedSimulation,
        action: { actionType: ECalendarSimulationAction.DeleteChargeOff, operationId: chargeOff.id },
      })
    },
  })

  return useCallback(
    async (chargeOff: ChargeOffOperation): Promise<SignedCalendarSimulation | null> => {
      resultRef.current = null
      return (await confirm.open(chargeOff)) ? resultRef.current : null
    },
    [confirm],
  )
}
